import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { t } from '@/i18n'
import { createBank, getBanks, type BankOut } from '@/lib/api'

interface Props {
  fiat?: string
  value: string | null
  onChange: (bankId: string | null, bank: BankOut | null) => void
}

export function BankPicker({ fiat, value, onChange }: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newCountry, setNewCountry] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const close = () => {
    setOpen(false)
    setCreating(false)
    setQuery('')
    setCreateError(null)
  }

  const banksQuery = useQuery({
    queryKey: ['banks', fiat],
    queryFn: () => getBanks(fiat ? { fiat } : undefined),
  })

  const createMutation = useMutation({
    mutationFn: () => {
      const payload: { name: string; country?: string } = { name: newName.trim() }
      if (newCountry.trim()) payload.country = newCountry.trim()
      return createBank(payload)
    },
    onSuccess: (bank) => {
      void queryClient.invalidateQueries({ queryKey: ['banks'] })
      onChange(bank.id, bank)
      setNewName('')
      setNewCountry('')
      close()
    },
    onError: (err) => {
      setCreateError(err instanceof Error ? err.message : t('bank.addFail'))
    },
  })

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  useEffect(() => {
    document.body.classList.toggle('overflow-hidden', open && window.innerWidth < 768)
    return () => document.body.classList.remove('overflow-hidden')
  }, [open])

  const banks = banksQuery.data ?? []
  const selected = banks.find((item) => item.id === value) ?? null
  const needle = query.trim().toLowerCase()

  const filtered = useMemo(() => {
    if (!needle) return banks
    return banks.filter((bank) => {
      const hay = [bank.name, bank.code, ...bank.aliases].join(' ').toLowerCase()
      return hay.includes(needle)
    })
  }, [banks, needle])

  const favorites = filtered.filter((item) => item.is_favorite)
  const rest = filtered.filter((item) => item.is_favorite === false)

  const pick = (bank: BankOut) => {
    onChange(bank.id, bank)
    close()
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-md border border-surface-border bg-surface px-3 py-2 text-left text-sm text-slate-200"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span>{selected ? selected.name : t('bank.choose')}</span>
        <span className="text-xs text-slate-500">{selected?.country ?? ''}</span>
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label={t('bank.closeList')}
            className="fixed inset-0 z-40 bg-black/50 md:bg-black/20"
            onPointerDown={(event) => {
              event.preventDefault()
              close()
            }}
          />
          <div className="fixed inset-x-0 bottom-0 z-50 max-h-[80vh] overflow-auto rounded-t-xl border border-surface-border bg-surface-raised p-3 shadow-xl md:absolute md:inset-auto md:top-full md:mt-1 md:max-h-80 md:w-full md:rounded-md">
            <div className="mb-2 flex items-center justify-between md:hidden">
              <span className="text-sm text-slate-200">{t('bank.title')}</span>
              <button type="button" className="text-xs text-slate-400" onClick={close}>
                {t('common.close')}
              </button>
            </div>
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('bank.search')}
              className="mb-2 w-full rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
            />
            <div className="max-h-56 overflow-auto md:max-h-48">
              {favorites.length > 0 && (
                <Group title={t('bank.fav')}>
                  {favorites.map((bank) => (
                    <BankRow key={bank.id} bank={bank} active={bank.id === value} onSelect={() => pick(bank)} />
                  ))}
                </Group>
              )}
              <Group title={t('bank.all')}>
                {rest.map((bank) => (
                  <BankRow key={bank.id} bank={bank} active={bank.id === value} onSelect={() => pick(bank)} />
                ))}
                {filtered.length === 0 && (
                  <div className="px-2 py-3 text-xs text-slate-500">{t('bank.none')}</div>
                )}
              </Group>
            </div>
            {creating ? (
              <div className="mt-2 space-y-2 border-t border-surface-border pt-2">
                <input
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                  placeholder={t('bank.name')}
                  className="w-full rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
                />
                <input
                  value={newCountry}
                  onChange={(event) => setNewCountry(event.target.value.toUpperCase())}
                  placeholder={t('bank.country')}
                  maxLength={2}
                  className="w-full rounded-md border border-surface-border bg-surface px-2 py-1.5 text-sm"
                />
                {createError && <p className="text-xs text-loss">{createError}</p>}
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="rounded-md bg-slate-100 px-3 py-1 text-xs text-slate-900 disabled:opacity-50"
                    disabled={!newName.trim() || createMutation.isPending}
                    onClick={() => createMutation.mutate()}
                  >
                    {createMutation.isPending ? t('bank.adding') : t('bank.add')}
                  </button>
                  <button type="button" className="text-xs text-slate-500" onClick={() => setCreating(false)}>
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="mt-2 w-full border-t border-surface-border pt-2 text-left text-xs text-slate-400 hover:text-slate-200"
                onClick={() => setCreating(true)}
              >
                {t('bank.addOwn')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-2">
      <div className="px-2 pb-1 text-[11px] uppercase tracking-wider text-slate-600">{title}</div>
      {children}
    </div>
  )
}

function BankRow({
  bank,
  active,
  onSelect,
}: {
  bank: BankOut
  active?: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onPointerDown={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onSelect()
      }}
      className={[
        'flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm',
        active ? 'bg-slate-700/50 text-slate-100' : 'text-slate-300 hover:bg-slate-700/30',
      ].join(' ')}
    >
      <span>
        {bank.name}
        {bank.is_own ? <span className="ml-2 text-[10px] text-slate-500">{t('bank.own')}</span> : null}
      </span>
      <span className="text-[11px] text-slate-500">{bank.country ?? '—'}</span>
    </button>
  )
}
